import { withAuthenticationRequired } from "@auth0/auth0-react";
import { store } from "../store";
import { useTranslation } from "react-i18next";
import {
  Grid,
  Paper,
  Typography,
  Box,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Inventory2,
  CheckCircle,
  Warning,
  Devices,
  Business,
  FilterList,
} from "@mui/icons-material";
import PacksTable from "../components/tables/Packs";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

function Packs(props) {
  const { t } = useTranslation("common");
  const packs = useSelector((state) => state.packs);
  const residences = useSelector((state) => state.residences);
  const establishments = useSelector((state) => state.establishments);

  const [selectedEstablishment, setSelectedEstablishment] = useState("all");

  // Fonction pour obtenir l'établissement d'un pack
  const getPackEstablishment = (pack) => {
    const residence = residences.find((r) => r.pack_id === pack.id);
    if (!residence) return null;
    return establishments.find(
      (e) => parseInt(e.id) === parseInt(residence.establishment_id)
    );
  };

  // Filtrer les packs selon l'établissement sélectionné
  const filteredPacks =
    selectedEstablishment === "all"
      ? packs
      : selectedEstablishment === "unassigned"
        ? packs.filter((pack) => !getPackEstablishment(pack))
        : packs.filter((pack) => {
            const estab = getPackEstablishment(pack);
            return estab && parseInt(estab.id) === parseInt(selectedEstablishment);
          });

  // Calculer les statistiques (basées sur les packs filtrés)
  const totalPacks = filteredPacks.length;
  const assignedPacks = filteredPacks.filter((pack) =>
    residences.some((residence) => residence.pack_id === pack.id)
  ).length;
  const unassignedPacks = totalPacks - assignedPacks;
  const totalDevices = filteredPacks.reduce(
    (acc, pack) => acc + (pack.devices?.length || 0),
    0
  );

  const statsCards = [
    {
      title: t("common.total"),
      value: totalPacks,
      icon: Inventory2,
      color: "#78a882",
      bgColor: alpha("#78a882", 0.08),
    },
    {
      title: t("common.assigned") || "Assignés",
      value: assignedPacks,
      icon: CheckCircle,
      color: "#6b9dad",
      bgColor: alpha("#6b9dad", 0.08),
    },
    {
      title: t("common.not_assigned") || "Non assignés",
      value: unassignedPacks,
      icon: Warning,
      color: unassignedPacks > 0 ? "#c4a574" : "#78a882",
      bgColor: alpha(unassignedPacks > 0 ? "#c4a574" : "#78a882", 0.08),
    },
    {
      title: t("common.devices"),
      value: totalDevices,
      icon: Devices,
      color: "#8a9ba8",
      bgColor: alpha("#8a9ba8", 0.08),
    },
  ];

  useEffect(() => {
    store.dispatch({
      type: "pageTitle/update",
      payload: t("common.packs"),
    });

    let pageActions = [
      {
        name: t("common.add_new", { name: t("common.pack") }),
        icon: "Add",
        url: "/add/pack",
      },
    ];

    store.dispatch({ type: "pageActions/update", payload: pageActions });
  }, [t]);

  return (
    <Grid container spacing={3}>
      {/* Cartes statistiques */}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          {statsCards.map((stat, index) => (
            <Grid item xs={6} sm={3} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  background: stat.bgColor,
                  border: `1px solid ${alpha(stat.color, 0.2)}`,
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 4px 20px ${alpha(stat.color, 0.2)}`,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: alpha(stat.color, 0.15),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <stat.icon sx={{ color: stat.color, fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: stat.color, lineHeight: 1 }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", mt: 0.5 }}
                    >
                      {stat.title}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Grid>

      {/* Filtre par établissement */}
      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            backgroundColor: alpha("#7a8a7a", 0.05),
            border: `1px solid ${alpha("#7a8a7a", 0.1)}`,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FilterList sx={{ color: "#7a8a7a", fontSize: 20 }} />
            <Typography variant="body2" sx={{ color: "#9aa89a", fontWeight: 500 }}>
              {t("common.filter_table")}
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel id="establishment-filter-label">
              {t("common.establishment")}
            </InputLabel>
            <Select
              labelId="establishment-filter-label"
              value={selectedEstablishment}
              label={t("common.establishment")}
              onChange={(e) => setSelectedEstablishment(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: alpha("#7a8a7a", 0.3),
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: alpha("#7a8a7a", 0.5),
                },
              }}
            >
              <MenuItem value="all">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Inventory2 sx={{ fontSize: 18, color: "#78a882" }} />
                  {t("common.all")} ({packs.length})
                </Box>
              </MenuItem>
              <MenuItem value="unassigned">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Warning sx={{ fontSize: 18, color: "#c4a574" }} />
                  {t("common.not_assigned")} (
                  {packs.filter((p) => !getPackEstablishment(p)).length})
                </Box>
              </MenuItem>
              {establishments.map((estab) => {
                const count = packs.filter((p) => {
                  const e = getPackEstablishment(p);
                  return e && parseInt(e.id) === parseInt(estab.id);
                }).length;
                return (
                  <MenuItem key={estab.id} value={estab.id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Business sx={{ fontSize: 18, color: "#9a8878" }} />
                      {estab.name} ({count})
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {selectedEstablishment !== "all" && (
            <Typography
              variant="caption"
              sx={{
                color: "#9aa89a",
                ml: "auto",
              }}
            >
              {filteredPacks.length} {t("common.packs").toLowerCase()}
            </Typography>
          )}
        </Paper>
      </Grid>

      {/* Tableau des packs */}
      <Grid item xs={12}>
        <PacksTable filteredPacks={filteredPacks} />
      </Grid>
    </Grid>
  );
}

export default withAuthenticationRequired(Packs);
